<template lang="pug">
div
  v-card.round-xl.pa-5.mt-4(outlined)
    div.d-flex.mb-1(v-if="history.company && history.company.slug !== company.slug")
      yz-badge.pa-1(border, size="sm" border-color="blue-500" text-color="blue-500" color="blue-100") {{ history.company.name }}
    div.d-flex.align-center
      div.text-xl.font-medium {{ history.record_date | humanizeDate }}, {{ history.record_date | displayTime2 }}
      v-spacer
      template(v-if="history.provider")
        div.text-xs.font-medium.neutral700--text By
        y-chip.ml-1(:label="history.provider.name" size="xs" chip-class="pa-1" color="blue")
          template(#prepend)
            v-avatar.mr-1(size="16" color="neutral300")
              template(v-if="history.provider.profile_image")
                v-img(:src="`https://img.yezza.io/50x50/${history.provider.profile_image.path}`")
              v-icon(v-else color="neutral500" size="15") $yzi-user-03
    div.mt-5
      //- visit note
      div.text-xs.font-medium.neutral500--text Visit note
      div.text-sm.neutral700--text.mt-1(v-if="history.consultation && history.consultation.case") {{ history.consultation.case }}
      div.text-sm.neutral700--text.mt-1(v-else) -
      //- diagnosis
      div.text-xs.font-medium.neutral500--text.mt-6 Diagnosis
      template(v-if="history.consultation && history.consultation.diagnoses.length")
        ol.mt-1
          template(v-for="(diagnose, idx) in history.consultation.diagnoses")
            li.text-sm.neutral700--text(:key="`dg-${idx}`") {{ diagnose }}
      div.text-sm.neutral700--text.mt-1(v-else) -
      //- vital signs
      div.text-xs.font-medium.neutral500--text.mt-6 Vital signs
      v-card.mt-1(outlined)
        v-simple-table(dense)
          thead
            tr
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") Ht (cm)
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") Wt (kg)
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") Temp (C)
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") BP (mmHg)
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") HR (bpm)
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") SpO2
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") BG mmol/L
              th.neutral500--text.px-2(style="text-transform: none !important; font-size: 10px !important;") RR
          tbody
            template(v-if="history.vital_signs.length")
              template(v-for="(vitalsign, idx) in history.vital_signs")
                tr(:key="`vs-${idx}`")
                  td.text-xs.neutral700--text.px-2 {{ parseFloat(vitalsign.height) || '-' }}
                  td.text-xs.neutral700--text.px-2 {{ parseFloat(vitalsign.weight) || '-' }}
                  td.text-xs.neutral700--text.px-2 {{ vitalsign.temperature ? `${parseFloat(vitalsign.temperature)}°` : '-' }}
                  td.text-xs.neutral700--text.px-2 {{ vitalsign.bp_systolic ? `${vitalsign.bp_systolic}/ ${vitalsign.bp_diastolic}` : '-' }}
                  td.text-xs.neutral700--text.px-2 {{ vitalsign.pulse ? `${vitalsign.pulse }` : '-' }}
                  td.text-xs.neutral700--text.px-2 {{ vitalsign.spo2 ? `${vitalsign.spo2 }%` : '-' }}
                  td.text-xs.neutral700--text.px-2 {{ vitalsign.blood_glucose || '-' }}
                  td.text-xs.neutral700--text.px-2 {{ vitalsign.respiratory_rate || '-' }}
            template(v-else)
              tr
                td.text-xs.neutral700--text.px-2.text-center(colspan="8") No vital signs recorded.
      //- case note
      div.text-xs.font-medium.neutral500--text.mt-6 Case note
      div.text-sm.neutral700--text.mt-1(v-if="history.consultation && history.consultation.note")
        div(v-html="history.consultation.note" style="display: flow-root;")
      div.text-sm.neutral700--text.mt-1(v-else) -
      //- treatment
      div.d-flex.align-center.mt-6
        div.text-xs.font-medium.neutral500--text Treatment
        v-spacer
        template(v-if="history.treatments.length && !isFromOtherCompany")
          template(v-if="addTreatmentMode")
            y-button(label="Cancel" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="danger500" unset-min-width size="xxs" height="24" button-class="px-2" @click="addTreatmentMode = false")
            y-button.ml-2(label="Add to current plan" unset-min-width dark size="xxs" height="24" button-class="px-2" :loading="isLoading" @click="addPastTreatment")
          y-button(v-else label="Select to add to current plan" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="addTreatmentMode = true")
      div.mt-1(v-if="history.treatments.length")
        template(v-for="(treatment, index) in history.treatments")
          div(:key="`trtm-${index}`")
            v-divider(v-if="index")
            div.py-2
              v-row(no-gutters)
                v-col.shrink(v-if="addTreatmentMode")
                  div(style="margin-top: -2px;")
                    y-checkbox(v-model="treatment.selected" :disabled="['bundle', 'documents'].includes(treatment.product.type)") {{ treatment.name }}
                v-col.grow
                  div.d-flex.align-center
                    div.text-xs.font-semibold.neutral700--text {{ treatment.name }}
                      span.ml-1 x{{ treatment.quantity }}
                    y-chip.ml-2(v-if="checkActiveMadication(treatment)" label="Active" size="xs" chip-class="pa-1" color="green")
                      template(#prepend)
                        v-avatar.mr-1(size="6" color="success500")
                    v-spacer
                    div.text-xs.neutral700--text {{ (treatment.price * treatment.quantity) | displayPrice($currency) }}
                  //- packages children
                  div.mt-1(v-if="treatment.product.type === 'bundle'")
                    template(v-for="(bundle, idx) in treatment.bundles")
                      div.d-flex(:key="`phpckg-${index}-${idx}`")
                        v-icon.ml-2.mr-1(size="14" color="neutral700") $yzi-corner-down-right
                        div
                          div.text-xxs.font-medium.neutral700--text.ellipsis-text {{ bundle.name }}
                            span.ml-1(v-if="bundle.quantity_per_set > 1") x{{ bundle.quantity_per_set }}
                          div.text-xxs.neutral700--text(v-if="Object.keys(bundle.extra_data).length") {{ getTreatmentExtraData(bundle.extra_data) }}
                  //- extra data for non-package
                  div.text-xs.neutral700--text.mt-1(v-else-if="Object.keys(treatment.extra_data).length") {{ getTreatmentExtraData(treatment.extra_data) }}
        div.d-flex.justify-end.mt-3
          div.text-md
            span.font-medium.neutral500--text Total
            span.font-semibold.neutral700--text.ml-4 {{ (history.treatments.reduce((acc, item) => acc + (item.price * item.quantity), 0)) | displayPrice($currency) }}
      div.mt-1(v-else)
        div.text-xs.neutral700--text No treatment plan recorded.
      //- uploaded file
      div.text-xs.font-medium.neutral500--text.mt-5 Uploaded file
      div.mt-1(v-if="history.consultation && history.consultation.attachments.length")
        template(v-for="(attachment, idx) in history.consultation.attachments")
          div(:key="`attc-${idx}`")
            v-divider(v-if="idx")
            div.d-flex.align-center.py-2
              div.mr-2(v-if="attachment.document.type.includes('image')")
                v-img(:src="attachment.document.file" width="20" height="20" contain)
              v-icon.mr-2(v-else-if="attachment.document.type.includes('pdf')" color="neutral500" size="20") mdi-file-pdf-box
              div.text-xs.font-semibold.neutral700--text {{ attachment.name }} - {{ attachment.document.filename }}
              v-spacer
              v-btn.mr-2(v-if="!isFromOtherCompany" icon x-small color="neutral500" @click="deleteItem(attachment)")
                v-icon(size="16") $yzi-trash-03
              v-btn.mr-2(icon x-small color="neutral500" @click="downloadFile(attachment)")
                v-icon(size="16") $yzi-download-01
              v-btn(icon x-small color="neutral500" @click="viewFile(attachment)")
                v-icon(size="16") $yzi-eye
      div.mt-1(v-else)
        div.text-xs.neutral700--text No file uploaded.
      //- dispense note
      div.text-xs.font-medium.neutral500--text.mt-5 Dispense note
      div.text-sm.neutral700--text.mt-1(v-if="history.registration && history.registration.checkout_note") {{ history.registration.checkout_note }}
      div.text-sm.neutral700--text.mt-1(v-else) -
      //- appointment
      template(v-if="history.registration && history.registration.appointment")
        div.text-xs.font-medium.neutral500--text.mt-5 Appointment
        v-alert.mt-1.mb-0.py-1(border="left" colored-border dense color="purple600" style="background-color: var(--v-neutral200);")
          div.d-flex.align-center
            div
              y-chip(:label="history.registration.appointment.provider.name" size="xs" chip-class="pa-1")
                template(#prepend)
                  v-avatar.mr-1(size="16" color="neutral300")
                    template(v-if="history.registration.appointment.provider.profile_image")
                      v-img(:src="`https://img.yezza.io/50x50/${history.registration.appointment.provider.profile_image.path}`")
                    v-icon(v-else color="neutral500" size="15") $yzi-user-03
              div.text-xs.font-medium.neutral700--text.ml-1 {{ getAppointmentServices(history.registration.appointment) }}
            v-spacer
            div.text-xs.neutral500--text.no-wrap.ml-2 {{ history.registration.appointment.start | humanizeDate }}

  .dialogs
    view-image-dialog(v-if="showViewImageDialog" :open.sync="showViewImageDialog" :image="selectedFile" @closed="selectedFile = null")
    delete-attachment-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :pid="history.lead_pid" :file="selectedFile" @deleted="handleDeletedItem")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'
import DeleteAttachmentDialog from '@/components/clinic/dialogs/DeleteAttachmentDialog.vue'

export default {
  components: { ViewImageDialog, DeleteAttachmentDialog },
  props: {
    patientHistory: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      isLoading: false,
      addTreatmentMode: false,
      showViewImageDialog: false,
      showDeleteDialog: false,
      selectedFile: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      registration: 'treatment/getRegistration',
      activeMedications: 'treatment/getActiveMedications',
    }),
    history: {
      get () {
        return this.patientHistory
      },
      set (value) {
        this.$emit('update:patientHistory', value)
      }
    },
    isFromOtherCompany() {
      return this.history?.company?.slug !== this.company?.slug
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchTreatments () {
      this.isFetching = true
      let params = {
        fields: 'treatments'
      }
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, { params })
        .then(resp => {
          this.$store.commit('treatment/setTreatments', resp.data.treatments)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    getTreatmentExtraData (extra_data) {
      let data = []
      if (extra_data.indication) { data.push(extra_data.indication) }
      if (extra_data.dosage_value && extra_data.dosage_unit) { data.push(`${extra_data.dosage_value} ${extra_data.dosage_unit}`) }
      if (extra_data.frequency) { data.push(extra_data.frequency) }
      if (extra_data.duration) { data.push(extra_data.duration) }
      if (extra_data.instruction) { data.push(extra_data.instruction) }
      if (extra_data.precaution) { data.push(extra_data.precaution) }
      return data.join(' | ')
    },
    getAppointmentServices (appointment) {
      let services = []
      if (appointment.services.length) {
        services = appointment.services.map(s => s.name)
      }
      return services.join(', ')
    },
    checkActiveMadication (treatment) {
      if (treatment.product.medication && treatment.product.medication) {
        if (this.activeMedications.find(f => f.m_id === treatment.product.medication.id)) {
          return true
        }
      }
      return false
    },
    checkMedicine (treatment) {
      if (treatment.product.medication && treatment.product.medication.id) {
        return true
      }
      return false
    },
    deleteItem (item) {
      this.showDeleteDialog = true
      this.selectedFile = item
    },
    downloadFile (item) {
      window.open(item.document.file, '_blank')
    },
    viewFile (item) {
      if (item.document.type.includes('image')) {
        this.viewImage(item.document.file)
      } else if (item.document.type.includes('pdf')) {
        this.viewPDF(item.document.file)
      }
    },
    viewImage (image) {
      this.selectedFile = image
      this.showViewImageDialog = true
    },
    viewPDF (file) {
      const url = `https://docs.google.com/viewer?url=${file}`
      window.open(url,'_blank')
    },
    handleDeletedItem () {
      this.selectedFile = null
      this.$emit('updated')
    },
    async addPastTreatment () {
      this.isLoading = true
      let products = []
      let treatments = this.history.treatments.filter(f => f.selected)
      if (treatments.length === 0) {
        this.showMessage(['error', 'Please select at least one item'])
        this.isLoading = false
        return
      }
      for (let i = 0; i < treatments.length; i++) {
        let data = {
          product: treatments[i].product.id,
          quantity: treatments[i].quantity,
          price: 0
        }
        let params = { 
          company: this.company.id,
          product: treatments[i].product.id
        }
        if (this.registration?.default_payment?.type !== 'self-pay') {
          params.payment_methods = this.registration.default_payment.panel.id
        } else {
          params.payment_channel = 'self-pay'
        }
        await this.$axios.get('/product_pricetiers', { params })
          .then(resp => {
            if (resp.data.count) {
              data.price = resp.data.results[0].price || 0
              data.extra_data = {
                price_tier: resp.data.results[0].id,
                price_tier_name: resp.data.results[0].name,
              }
            }
          })
          .catch(() => {})
        if (this.checkMedicine(treatments[i])) {
          data.extra_data = {
            ...data.extra_data,
            dosage_unit: treatments[i].product.medication.dosage_unit,
            dosage_value: treatments[i].product.medication.dosage_value,
            duration: treatments[i].product.medication.duration,
            frequency: treatments[i].product.medication.frequency,
            indication: treatments[i].product.medication.indication,
            instruction: treatments[i].product.medication.instruction,
            precaution: treatments[i].product.medication.precaution,
          }
        }
        products.push(data)
        treatments[i].selected = false
      }
      let payload = { products: products }
      const endpoint = `/leads/${this.leadPid}`
      await this.$axios.patch(endpoint, payload)
        .then(resp2 => {
          this.showMessage(['success', 'Items added to treatment plan'])
          this.updateActivityLog(endpoint, payload, resp2.data, 'PATCH', resp2.data.id, 'consultation', `Update treatment plan from patient history (${this.leadPid})`)
          this.fetchTreatments()
        })
        .catch((err) => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.status === 400 && err.response?.data?.code === '444101') {
            message = 'Item quantity more than available stock. Please adjust the quantity'
          }       
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        })
      this.isLoading = false
      this.addTreatmentMode = false
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>

</style>